import dayjs from 'dayjs';
import * as Yup from 'yup';

import {
  BACKGROUND_TYPES,
  CARD_ROW_GAP,
  DATE_FORMAT,
  DEFAULT_FONT_FAMILY,
  DEFAULT_LANGUAGE,
  FIELD_TYPES,
  FULL_ROW_CARD_WIDTH,
  GRADIENT_DIRECTION_MAP,
  MAX_CARDS_PER_ROW,
  PATH_END_STEP_COUNT,
  SHORT_ROW_CARD_WIDTH,
} from './constants';
import { getTranslation } from './translations';

export const toSnakeCase = obj => {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => {
      let snakeKey = key.toLowerCase().replace(/\s+/g, '_');
      return [snakeKey, value];
    })
  );
};

export const getEmbedWebsiteUrl = () => {
  if (typeof window === 'undefined') return '';
  const locationParam = new URLSearchParams(window.location.search).get('location');
  if (locationParam) return locationParam;
  return typeof document !== 'undefined' && document.referrer ? document.referrer : window.location.href;
};

const isValidDate = value => value instanceof Date && !isNaN(value.getTime());

export const getFileNameFromUrl = url => {
  if (typeof url !== 'string') return '';

  const fileName = url.split('/').pop()?.split('?')[0] || '';
  const dotIndex = fileName.lastIndexOf('.');
  const underscoreIndex = fileName.lastIndexOf('_');

  if (dotIndex === -1 || underscoreIndex === -1) return fileName;

  const name = fileName.slice(0, underscoreIndex);
  const extension = fileName.slice(dotIndex);

  return `${name}${extension}`;
};

export const replaceWhiteSpaceWithUnderscore = str => {
  return str.replace(/\s+/g, '_');
};

/**
 * Language order used to resolve a per-language label dict to a single stable string.
 * Matches the backend `settings.LANGUAGES` order so the submitted response key never
 * depends on which language the field was authored in.
 */
export const PRIMARY_LABEL_LANGUAGE_ORDER = ['fi', 'en', 'de', 'sv'];

/**
 * Resolves a lead-field label to a single plain string.
 * Mirrors the backend `primary_label_text`: returns the first non-empty value in
 * PRIMARY_LABEL_LANGUAGE_ORDER. Accepts a legacy plain string for backward safety.
 *
 * @param {Object|string} label - Per-language label dict or a legacy plain string.
 * @returns {string} The primary-language label text, or an empty string.
 */
export const primaryLabelText = label => {
  if (label && typeof label === 'object') {
    const code = PRIMARY_LABEL_LANGUAGE_ORDER.find(langCode => label[langCode]);
    return code ? label[code] : '';
  }
  return typeof label === 'string' ? label : '';
};

/**
 * Returns the initial values for the fields based on the provided fields.
 *
 * @param {Array} fields - The fields array.
 * @returns {Object} The initial values object.
 */

export const getInitialValues = fields => {
  const values = {};
  fields?.forEach(({ label: rawLabel, type, isMultiSelect }) => {
    const label = primaryLabelText(rawLabel);
    if (!label) return;
    const fieldType = type?.toLowerCase?.() || type;
    if (fieldType === FIELD_TYPES.CONSENT) {
      values[label] = false;
      return;
    }
    values[label] = fieldType === FIELD_TYPES.DROPDOWN && isMultiSelect ? [] : '';
  });
  return values;
};

/**
 * Returns the localized value for a given custom and default value based on the provided active language.
 *
 * It attempts to find the value in the following order:
 *   1. customValue for the activeLang
 *   2. customValue in English ('en')
 *   3. defaultValue for the activeLang
 *   4. defaultValue in English ('en')
 *
 * @param {Object} customValue - The custom object containing key/value pairs of language codes and values.
 * @param {Object} defaultValue - The default object containing key/value pairs of language codes and values.
 * @param {string} activeLang - The active language code to use for localization.
 * @returns {string|undefined} The localized value based on the priority order, or undefined if not found.
 */
export const getLocalizedValue = (customValue, defaultValue, activeLang) =>
  customValue?.[activeLang] ||
  customValue?.[DEFAULT_LANGUAGE] ||
  defaultValue?.[activeLang] ||
  defaultValue?.[DEFAULT_LANGUAGE];

export const generateValidationSchema = (fields, activeLang) => {
  const schema = {};
  const requiredMsg = getTranslation('validation.required', activeLang);
  const invalidEmailMsg = getTranslation('validation.invalidEmail', activeLang);
  const numericOnlyMsg = getTranslation('validation.numericOnly', activeLang);

  fields?.forEach(({ label: rawLabel, type, isRequired, isMultiSelect }) => {
    const label = primaryLabelText(rawLabel);
    if (!label) return;
    const fieldType = type?.toLowerCase?.() || type;
    let validator;

    if (fieldType === FIELD_TYPES.CONSENT) {
      validator = Yup.boolean();
    } else if (fieldType === FIELD_TYPES.DROPDOWN && isMultiSelect) {
      validator = Yup.array()
        .of(Yup.string())
        .test('nonEmptyArray', requiredMsg, value => !isRequired || (Array.isArray(value) && value.length > 0));
    } else if (fieldType === FIELD_TYPES.DROPDOWN) {
      validator = Yup.string().nullable();
      if (isRequired) {
        validator = validator.test(
          'required',
          requiredMsg,
          value => value !== null && value !== undefined && value !== ''
        );
      }
    } else {
      switch (fieldType) {
      case FIELD_TYPES.EMAIL:
        validator = Yup.string().email(invalidEmailMsg);
        break;
      case FIELD_TYPES.NUMBER:
        validator = Yup.string().matches(/^\d+$/, { message: numericOnlyMsg, excludeEmptyString: true });
        break;
      case FIELD_TYPES.UPLOAD:
        validator = Yup.mixed().test(
          'fileRequired',
          requiredMsg,
          value => !isRequired || (value && value.length > 0)
        );
        break;
      case FIELD_TYPES.DATE:
        validator = Yup.mixed().test('validDate', requiredMsg, value => (value ? isValidDate(value) : !isRequired));
        break;
      case FIELD_TYPES.TEXTAREA:
      default:
        validator = Yup.string();
      }

      if (isRequired && fieldType !== FIELD_TYPES.UPLOAD) {
        validator = validator.required(requiredMsg);
      }
    }

    schema[label] = validator;
  });
  return Yup.object().shape(schema);
};

/**
 * Sorts the fields by the sorting order.
 *
 * @param {Array} fields - The fields array.
 * @returns {Array} The sorted fields array.
 */

export const sortFieldsByOrder = fields =>
  [...(fields || [])].sort((a, b) => (a.sortingOrder ?? 0) - (b.sortingOrder ?? 0));

/**
 * Formats the date value based on the provided value.
 *
 * @param {string} value - The value to format.
 * @returns {string} The formatted date value.
 */

const formatDateValue = value => {
  if (isValidDate(value)) {
    return dayjs(value).format(DATE_FORMAT.STANDARD_DATE);
  }
  return value;
};

/**
 * Formats the values for the payload based on the provided values.
 *
 * @param {Object} values - The values object.
 * @returns {Object} The formatted values object.
 */

export const formatValuesForPayload = values =>
  Object.entries(values || {}).reduce((acc, [key, value]) => {
    acc[key] = formatDateValue(value);
    return acc;
  }, {});

/**
 * Formats the current time in the format of year-month-day hour:minute.
 *
 * @returns {string} The formatted current time.
 */

export const formatTimeStamp = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

export const getPathBackgroundStyle = theme => {
  if (!theme) return {};

  const {
    fontFamily,
    backgroundType,
    backgroundColor,
    backgroundImage,
    gradientDirection,
    gradientPrimaryColor,
    gradientSecondaryColor,
    headingTextColor,
  } = theme;

  const styles = { fontFamily: fontFamily || DEFAULT_FONT_FAMILY, color: headingTextColor };

  if (backgroundType === BACKGROUND_TYPES.GRADIENT && gradientPrimaryColor && gradientSecondaryColor) {
    const direction = GRADIENT_DIRECTION_MAP[gradientDirection] || GRADIENT_DIRECTION_MAP.VERTICAL;
    styles.backgroundImage = `linear-gradient(${direction}, ${gradientPrimaryColor}, ${gradientSecondaryColor})`;
  } else if (backgroundType === BACKGROUND_TYPES.IMAGE && backgroundImage?.image) {
    styles.backgroundImage = `url(${backgroundImage.image})`;
    styles.backgroundSize = 'cover';
    styles.backgroundPosition = 'center';
    styles.backgroundRepeat = 'no-repeat';
    styles.backgroundColor = backgroundColor;
  } else {
    styles.backgroundColor = backgroundColor;
  }

  return styles;
};

/**
 * Where the visitor stands on the current branch: the steps behind them plus the longest run of
 * steps still ahead, ending in the form or link screen. Picking a shorter branch moves the bar
 * forward, never back.
 */
export const getPathProgress = (stepsById, currentStepId, depth) => {
  const longestRemaining = stepId => {
    const cards = stepsById.get(stepId)?.cards || [];
    return cards.reduce((longest, card) => {
      const remaining = card.nextStep ? 1 + longestRemaining(card.nextStep) : 0;
      return Math.max(longest, remaining);
    }, 0);
  };

  return { current: depth, total: depth + longestRemaining(currentStepId) + PATH_END_STEP_COUNT };
};

export const progressPercent = progress => (progress ? (progress.current / progress.total) * 100 : undefined);

export const hexToLottieColor = hex => {
  const r = parseInt(hex?.slice(1, 3), 16) / 255;
  const g = parseInt(hex?.slice(3, 5), 16) / 255;
  const b = parseInt(hex?.slice(5, 7), 16) / 255;
  return [r, g, b, 1];
};

/**
 * Recolors a lottie animation based on the provided fill and stroke colors.
 *
 * @param {Object} animationData - The lottie animation data.
 * @param {string} fillHex - The fill hex color.
 * @param {string} strokeHex - The stroke hex color.
 * @returns {Object} The recolored animation data.
 */
export const recolorLottie = (animationData, fillHex, strokeHex) => {
  const fillColor = hexToLottieColor(fillHex);
  const strokeColor = hexToLottieColor(strokeHex);
  const cloned = structuredClone(animationData);

  const traverse = obj => {
    if (!obj || typeof obj !== 'object') return;

    if (obj.ty === 'fl' && obj.c?.k?.length === 4) {
      obj.c.k = fillColor;
    }

    if (obj.ty === 'st' && obj.c?.k?.length === 4) {
      obj.c.k = strokeColor;
    }

    Object.values(obj).forEach(traverse);
  };

  traverse(cloned);
  return cloned;
};

// Splits cards into rows of at most maxPerRow, keeping the rows as even as possible (5 → 3 + 2, 7 → 4 + 3).
export const balancedRows = (items, maxPerRow) => {
  const rowCount = Math.ceil(items.length / maxPerRow);
  const perRow = Math.ceil(items.length / rowCount);
  return Array.from({ length: rowCount }, (__, index) => items.slice(index * perRow, (index + 1) * perRow));
};

const cardsThatFit = (width, cardWidth) => Math.floor((width + CARD_ROW_GAP) / (cardWidth + CARD_ROW_GAP));

// How many cards a row can hold at this width: four of the narrow cards, otherwise as many wide ones as fit.
export const cardsPerRow = width => {
  if (!width) return MAX_CARDS_PER_ROW;
  if (cardsThatFit(width, FULL_ROW_CARD_WIDTH) >= MAX_CARDS_PER_ROW) return MAX_CARDS_PER_ROW;
  return Math.max(1, Math.min(MAX_CARDS_PER_ROW - 1, cardsThatFit(width, SHORT_ROW_CARD_WIDTH)));
};
